import { useState, useCallback } from 'react'
import { uploadDocument, UploadError } from '@/services/uploadApi'
import { isValidFileType } from '@/types/upload'
import type { PendingFile, UploadStatus } from '@/types/upload'

export interface UseUploadOptions {
  businessId: string
  maxConcurrent?: number
  forceReprocess?: boolean
}

export interface UseUploadReturn {
  pendingFiles: PendingFile[]
  addFiles: (files: File[]) => { valid: File[]; invalid: File[] }
  removeFile: (id: string) => void
  startUpload: () => Promise<void>
  clearCompleted: () => void
  isUploading: boolean
  completedCount: number
  errorCount: number
  duplicateCount: number
  reprocessingCount: number
  validatedCount: number
}

/**
 * Hook for managing file uploads with progress tracking.
 * Handles file validation, concurrent uploads, and status tracking.
 */
export function useUpload(options: UseUploadOptions): UseUploadReturn {
  const { businessId, maxConcurrent = 3, forceReprocess } = options
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [isUploading, setIsUploading] = useState(false)

  // Add files to the pending list with validation
  const addFiles = useCallback((files: File[]) => {
    const valid: File[] = []
    const invalid: File[] = []

    files.forEach((file) => {
      if (isValidFileType(file)) {
        valid.push(file)
      } else {
        invalid.push(file)
      }
    })

    if (valid.length > 0) {
      const newPendingFiles: PendingFile[] = valid.map((file) => ({
        id: crypto.randomUUID(),
        file,
        status: 'pending' as UploadStatus,
        progress: 0,
      }))

      setPendingFiles((prev) => [...prev, ...newPendingFiles])
    }

    return { valid, invalid }
  }, [])

  // Remove a file from the pending list
  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id))
  }, [])

  // Update a specific file's state
  const updateFile = useCallback((id: string, updates: Partial<PendingFile>) => {
    setPendingFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    )
  }, [])

  // Upload a single file
  const uploadSingleFile = useCallback(
    async (pendingFile: PendingFile) => {
      updateFile(pendingFile.id, { status: 'uploading', progress: 0 })

      try {
        const result = await uploadDocument({
          file: pendingFile.file,
          businessId,
          forceReprocess,
          onProgress: (progress) => {
            updateFile(pendingFile.id, { progress })
          },
        })

        const status: UploadStatus = result.is_reprocessing
          ? 'reprocessing'
          : result.is_duplicate
            ? 'duplicate'
            : 'success'
        updateFile(pendingFile.id, {
          status,
          progress: 100,
          result: {
            id: result.id,
            s3_key: result.s3_key,
            checksum: result.checksum,
            created_at: result.created_at,
            document_status: result.document_status,
            is_duplicate: result.is_duplicate,
            is_reprocessing: result.is_reprocessing,
            message: result.message,
          },
        })
      } catch (error) {
        // A verified (VALIDATED) document is intentionally not reprocessed by the API.
        // Surface it as its own status rather than a generic failure.
        if (error instanceof UploadError && error.code === 'VALIDATED_IMMUTABLE') {
          updateFile(pendingFile.id, {
            status: 'validated',
            error: error.message,
          })
          return
        }

        const errorMessage =
          error instanceof UploadError
            ? error.message
            : 'Unknown error occurred'
        updateFile(pendingFile.id, {
          status: 'error',
          error: errorMessage,
        })
      }
    },
    [businessId, forceReprocess, updateFile]
  )

  // Start uploading all pending files with concurrency limit
  const startUpload = useCallback(async () => {
    const pending = pendingFiles.filter((f) => f.status === 'pending')
    if (pending.length === 0) return

    setIsUploading(true)

    // Process files in batches based on maxConcurrent
    const batches: PendingFile[][] = []
    for (let i = 0; i < pending.length; i += maxConcurrent) {
      batches.push(pending.slice(i, i + maxConcurrent))
    }

    for (const batch of batches) {
      await Promise.all(batch.map((file) => uploadSingleFile(file)))
    }

    setIsUploading(false)
  }, [pendingFiles, maxConcurrent, uploadSingleFile])

  // Clear completed, duplicate, reprocessing, and validated files from the list
  const clearCompleted = useCallback(() => {
    setPendingFiles((prev) =>
      prev.filter(
        (f) =>
          f.status !== 'success' &&
          f.status !== 'duplicate' &&
          f.status !== 'reprocessing' &&
          f.status !== 'validated'
      )
    )
  }, [])

  // Calculate counts
  const completedCount = pendingFiles.filter((f) => f.status === 'success').length
  const errorCount = pendingFiles.filter((f) => f.status === 'error').length
  const duplicateCount = pendingFiles.filter((f) => f.status === 'duplicate').length
  const reprocessingCount = pendingFiles.filter((f) => f.status === 'reprocessing').length
  const validatedCount = pendingFiles.filter((f) => f.status === 'validated').length

  return {
    pendingFiles,
    addFiles,
    removeFile,
    startUpload,
    clearCompleted,
    isUploading,
    completedCount,
    errorCount,
    duplicateCount,
    reprocessingCount,
    validatedCount,
  }
}
